import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import VoiceRecorder from './VoiceRecorder';
import EntityComment from './EntityComment';
import ClassifiedNotes from './ClassifiedNotes';
import './HorseDetail.css';

interface HorseProfile {
  age: number | null;
  sex: string | null;
  colour: string | null;
  country: string | null;
  sire: string | null;
  dam: string | null;
  sireOfDam: string | null;
  foalDate: string | null;
  owners: string | null;
  trainer: string | null;
  silkColours: string | null;
}

interface CareerStats {
  starts: number;
  wins: number;
  seconds: number;
  thirds: number;
  winPct: number | null;
  placePct: number | null;
  prizeMoney: number;
  formString: string;
}

interface RecordRow {
  label: string;
  starts: number;
  firsts: number;
  seconds: number;
  thirds: number;
}

interface Connection {
  name: string | null;
  strikeRate: number | null;
  a2e: number | null;
  wins: number;
  runners: number;
}

interface Connections {
  trainer: Connection | null;
  jockey: Connection | null;
  combo: Connection | null;
}

interface PastRun {
  date: string | null;
  venue: string;
  raceName: string;
  raceClass: string;
  distance: number;
  trackCondition: string;
  position: number | null;
  starters: number;
  margin: number | null;
  barrier: number;
  weight: number | null;
  jockey: string;
  sp: number | null;
  openingSp: number | null;
  bf: number | null;
  tab: number | null;
  inRun: string;
  raceTime: string;
  stewards: string;
  winner: string;
  // Derived from the gaps between meetingDates (PF ships no "days since" field).
  daysSincePrev: number | null;
  spell: boolean;
  letup: boolean;
}

interface Engagement {
  date: string | null;
  venue: string;
  raceNumber: number;
  raceName: string;
  raceClass: string;
  distance: number;
  trackCondition: string;
  barrier: number;
  weight: number | null;
  jockey: string;
  trainer: string;
  number: number;
  sp: number | null;
}

interface HorseData {
  horseName: string;
  // False when no PuntingForm/breeding profile exists (e.g. harness/greyhound).
  hasProfile?: boolean;
  profile: HorseProfile;
  careerStats: CareerStats;
  records: RecordRow[];
  connections: Connections;
  raceHistory: PastRun[];
  // Days from the most recent run to today (the "16 days since last run" line).
  daysSinceLastRun: number | null;
  engagements: Engagement[];
}

const SEX_LABELS: { [key: string]: string } = {
  G: 'Gelding', M: 'Mare', H: 'Horse', F: 'Filly', C: 'Colt', R: 'Rig'
};

const HorseDetail: React.FC = () => {
  const { horseName } = useParams<{ horseName: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Basic runner/market data passed from the race page — used to show something
  // useful for runners with no PuntingForm profile (harness/greyhound).
  const navRunner = (location.state as { runner?: any } | null)?.runner;
  const [horseData, setHorseData] = useState<HorseData | null>(null);
  const [raceRunner, setRaceRunner] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [commentRefresh, setCommentRefresh] = useState(0);

  useEffect(() => {
    if (horseName) fetchHorseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [horseName]);

  // No PuntingForm profile (harness/greyhound) and no data passed via navigation
  // (e.g. a directly-opened/bookmarked link)? Pull the runner's basic data from
  // the race it came from (?from=Meeting/Race&date=).
  useEffect(() => {
    if (!horseData || horseData.hasProfile !== false || navRunner) return;
    const params = new URLSearchParams(location.search);
    const from = params.get('from');
    const date = params.get('date') || '';
    if (!from) return;
    const [meeting, raceNo] = from.split('/');
    if (!meeting || !raceNo) return;
    const norm = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, '');
    axios
      .get(`${API_BASE_URL}/api/races/${encodeURIComponent(meeting)}/${raceNo}${date ? `?date=${encodeURIComponent(date)}` : ''}`)
      .then((res) => {
        const runners = res.data?.runners || [];
        const m = runners.find((r: any) => norm(r.horse_name || '') === norm(horseName || ''));
        if (m) {
          setRaceRunner({
            horse_number: m.horse_number, jockey: m.jockey, trainer: m.trainer,
            barrier: m.barrier, weight: m.weight, sp_price: m.sp_price,
            fixed_odd_win: m.fixed_odd_win, fixed_odd_place: m.fixed_odd_place,
            tote_win: m.tote_win, tote_place: m.tote_place,
            bf_back: m.bf_back, bf_lay: m.bf_lay, bf_last_traded: m.bf_last_traded, bf_bsp: m.bf_bsp,
            meetingName: meeting, raceNumber: Number(raceNo),
          });
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [horseData]);

  const fetchHorseData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/api/horse/${encodeURIComponent(horseName!)}`);
      setHorseData(response.data);
      setLoading(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch horse data');
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
  };

  const getSexLabel = (sex: string | null) => (sex ? SEX_LABELS[sex] || sex : '');

  // PuntingForm encodes in-run positions as a raw string like
  // "settling_down,7;m800,7;m400,8;finish,3;". Parse it into the running order
  // punters read — Settle → 1200 → 800 → 400 → Finish — e.g. "7-7-8-3", with a
  // labelled tooltip. Only points actually present are shown.
  const IN_RUN_POINTS = [
    { key: 'settling_down', label: 'Settle' },
    { key: 'm1600', label: '1600m' },
    { key: 'm1200', label: '1200m' },
    { key: 'm800', label: '800m' },
    { key: 'm400', label: '400m' },
    { key: 'finish', label: 'Fin' },
  ];
  const parseInRun = (raw: string): { text: string; title: string } => {
    if (!raw) return { text: '—', title: '' };
    const map: { [k: string]: string } = {};
    raw.split(';').forEach((pair) => {
      const [k, v] = pair.split(',');
      if (k && v != null && v.trim() !== '') map[k.trim()] = v.trim();
    });
    const points = IN_RUN_POINTS.filter((p) => map[p.key] != null);
    if (points.length === 0) return { text: '—', title: '' };
    return {
      text: points.map((p) => map[p.key]).join('-'),
      title: points.map((p) => `${p.label} ${map[p.key]}`).join(' · '),
    };
  };

  if (loading) {
    return <div className="loading">Loading horse details...</div>;
  }

  if (error || !horseData) {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p>{error || 'Horse not found'}</p>
        <button onClick={() => navigate(-1)} className="back-button">Go Back</button>
      </div>
    );
  }

  const { profile, careerStats, records, connections, raceHistory, daysSinceLastRun, engagements } = horseData;
  const hasProfile = horseData.hasProfile !== false;
  const basicRunner = navRunner || raceRunner;
  const connectionRows: { label: string; conn: Connection | null }[] = [
    { label: 'Trainer', conn: connections?.trainer ?? null },
    { label: 'Jockey', conn: connections?.jockey ?? null },
    { label: 'T + J combo', conn: connections?.combo ?? null },
  ];
  const hasConnections = connectionRows.some((row) => row.conn);

  // "Back to Race" target: prefer the race we came from (passed as ?from=
  // Meeting/RaceNumber&date=), then this horse's first engagement, else the
  // dashboard — never a blind browser-history step.
  const handleBack = () => {
    const params = new URLSearchParams(location.search);
    const from = params.get('from');
    const date = params.get('date') || '';
    if (from) {
      const [meeting, raceNo] = from.split('/');
      if (meeting && raceNo) {
        navigate(
          `/race/${encodeURIComponent(meeting)}/${raceNo}` +
            (date ? `?date=${encodeURIComponent(date)}` : '')
        );
        return;
      }
    }
    const firstEng = engagements[0];
    if (firstEng?.venue) {
      navigate(
        `/race/${encodeURIComponent(firstEng.venue)}/${firstEng.raceNumber}` +
          (firstEng.date ? `?date=${encodeURIComponent(firstEng.date)}` : '')
      );
      return;
    }
    navigate('/');
  };
  const typeLabel = [profile.age ? `${profile.age}yo` : '', getSexLabel(profile.sex), profile.colour]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="horse-detail">
      <button onClick={handleBack} className="back-button">← Back to Race</button>

      {/* Horse Header */}
      <div className="horse-header">
        <h1 className="horse-name">{horseName}</h1>
        <span className="tab-badge">{hasProfile ? '🐴 PuntingForm Form Guide' : '🏁 Runner Info'}</span>
      </div>

      <section className="notes-section">
        <VoiceRecorder
          scope="horse"
          context={{ horseName }}
          title="Horse voice notes"
          subtitle={horseName}
          onTranscribed={() => setCommentRefresh((n) => n + 1)}
        />

        <EntityComment
          scope="horse"
          context={{ horseName }}
          title="Horse comment"
          refreshKey={commentRefresh}
        />

        <ClassifiedNotes mode="horse" horseName={horseName || ''} />
      </section>

      {!hasProfile && basicRunner && (() => {
        const odds = (v: any) => (v == null || v === 0 ? '—' : Number(v).toFixed(2));
        const wgt = basicRunner.weight
          ? (/kg$/i.test(String(basicRunner.weight)) ? basicRunner.weight : `${basicRunner.weight}kg`)
          : '—';
        const rows: { label: string; value: React.ReactNode }[] = [
          { label: 'Race', value: basicRunner.meetingName ? `${basicRunner.meetingName}${basicRunner.raceNumber ? ` R${basicRunner.raceNumber}` : ''}` : '—' },
          { label: 'No.', value: basicRunner.horse_number ?? '—' },
          { label: 'Jockey/Driver', value: basicRunner.jockey || '—' },
          { label: 'Trainer', value: basicRunner.trainer || '—' },
          { label: 'Barrier', value: basicRunner.barrier || '—' },
          { label: 'Weight', value: wgt },
          { label: 'SP', value: odds(basicRunner.sp_price) },
          { label: 'Fixed Win', value: odds(basicRunner.fixed_odd_win) },
          { label: 'Fixed Place', value: odds(basicRunner.fixed_odd_place) },
          { label: 'Tote Win', value: odds(basicRunner.tote_win) },
          { label: 'Tote Place', value: odds(basicRunner.tote_place) },
          { label: 'Betfair Back', value: odds(basicRunner.bf_back) },
          { label: 'Betfair Lay', value: odds(basicRunner.bf_lay) },
          { label: 'Betfair LTP', value: odds(basicRunner.bf_last_traded) },
          { label: 'Betfair BSP', value: odds(basicRunner.bf_bsp) },
        ];
        return (
          <div className="horse-summary">
            <h2 className="basic-runner-title">Runner &amp; Market Data</h2>
            <div className="summary-grid">
              {rows.map((r) => (
                <div className="summary-item" key={r.label}>
                  <label>{r.label}</label>
                  <span className="value">{r.value}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {!hasProfile && !basicRunner && (
        <div className="horse-no-profile">
          Limited data for this runner — no detailed form is available (PuntingForm covers
          thoroughbred racing only; harness and greyhound runners aren’t included).
        </div>
      )}

      {hasProfile && (
      <>
      {/* Summary Stats */}
      <div className="horse-summary">
        <div className="summary-grid">
          <div className="summary-item">
            <label>Career (W-2nd-3rd)</label>
            <span className="value">
              {careerStats.starts}: {careerStats.wins}-{careerStats.seconds}-{careerStats.thirds}
            </span>
          </div>
          <div className="summary-item">
            <label>Win / Place %</label>
            <span className="value">
              {careerStats.winPct != null ? `${Math.round(careerStats.winPct)}%` : '—'}
              {' / '}
              {careerStats.placePct != null ? `${Math.round(careerStats.placePct)}%` : '—'}
            </span>
          </div>
          <div className="summary-item">
            <label>Prize Money</label>
            <span className="value">${careerStats.prizeMoney.toLocaleString()}</span>
          </div>
          <div className="summary-item">
            <label>Last 10 starts</label>
            <span className="value form-string">{careerStats.formString || '—'}</span>
          </div>
          {daysSinceLastRun != null && (
            <div className="summary-item">
              <label>Days since last run</label>
              <span className="value">
                {daysSinceLastRun} day{daysSinceLastRun === 1 ? '' : 's'}
                {daysSinceLastRun >= 90 ? ' (resuming from spell)'
                  : daysSinceLastRun >= 56 ? ' (resuming from let-up)' : ''}
              </span>
            </div>
          )}
          {typeLabel && (
            <div className="summary-item">
              <label>Type</label>
              <span className="value">{typeLabel}</span>
            </div>
          )}
          {profile.trainer && (
            <div className="summary-item">
              <label>Trainer</label>
              <span className="value">{profile.trainer}</span>
            </div>
          )}
          {profile.sire && (
            <div className="summary-item">
              <label>Sire</label>
              <span className="value">{profile.sire}</span>
            </div>
          )}
          {profile.dam && (
            <div className="summary-item">
              <label>Dam</label>
              <span className="value">{profile.dam}</span>
            </div>
          )}
          {profile.sireOfDam && (
            <div className="summary-item">
              <label>Sire of Dam</label>
              <span className="value">{profile.sireOfDam}</span>
            </div>
          )}
          {profile.owners && (
            <div className="summary-item">
              <label>Owners</label>
              <span className="value">{profile.owners}</span>
            </div>
          )}
        </div>
      </div>

      {/* Run-by-run past performances (from /form/form) */}
      <div className="race-history">
        <h2>Form (Last {raceHistory.length || 0} Runs)</h2>
        {raceHistory.length === 0 ? (
          <p className="no-history">No past-run history available for this horse.</p>
        ) : (
          <div className="history-table-wrapper">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Fin</th>
                  <th>Date</th>
                  <th title="Days since the previous run">Days</th>
                  <th>Track</th>
                  <th>Dist</th>
                  <th>Cond</th>
                  <th>Class</th>
                  <th>Jockey</th>
                  <th>Wgt</th>
                  <th>Brr</th>
                  <th>Mgn</th>
                  <th title="Opening price on the day">Open</th>
                  <th title="Starting price">SP</th>
                  <th title="Betfair starting price">BF</th>
                  <th>In Run</th>
                  <th>Winner</th>
                </tr>
              </thead>
              <tbody>
                {raceHistory.map((run, index) => {
                  const inRun = parseInRun(run.inRun);
                  return (
                  <React.Fragment key={index}>
                  <tr className={run.position === 1 ? 'winner' : ''}>
                    <td className="position">
                      {run.position ? (
                        <span className={`position-badge position-${run.position}`}>
                          {run.position}{run.starters ? `/${run.starters}` : ''}
                        </span>
                      ) : '—'}
                    </td>
                    <td>{formatDate(run.date)}</td>
                    <td title={run.daysSincePrev != null ? `${run.daysSincePrev} days since previous run` : ''}>
                      {run.daysSincePrev != null ? `${run.daysSincePrev}d` : '—'}
                    </td>
                    <td className="venue">
                      {run.venue ? (
                        <Link to={`/?search=${encodeURIComponent(run.venue)}`} className="horse-link">
                          {run.venue}
                        </Link>
                      ) : '—'}
                    </td>
                    <td>{run.distance ? `${run.distance}m` : '—'}</td>
                    <td>{run.trackCondition || '—'}</td>
                    <td className="race-class">{run.raceClass || '—'}</td>
                    <td>{run.jockey || '—'}</td>
                    <td>{run.weight ? `${run.weight}kg` : '—'}</td>
                    <td>{run.barrier || '—'}</td>
                    <td>{run.margin != null ? `${run.margin}L` : '—'}</td>
                    <td>{run.openingSp != null ? `$${run.openingSp.toFixed(2)}` : '—'}</td>
                    <td>{run.sp != null ? `$${run.sp.toFixed(2)}` : '—'}</td>
                    <td>{run.bf != null ? `$${run.bf.toFixed(2)}` : '—'}</td>
                    <td className="in-run" title={inRun.title}>{inRun.text}</td>
                    <td className="race-class">{run.winner || '—'}</td>
                  </tr>
                  {(run.spell || run.letup) && run.daysSincePrev != null && (
                    <tr className="spell-row">
                      <td colSpan={16}>
                        {run.spell ? '⟢ Spell' : '⟢ Let-up'} — {run.daysSincePrev} days
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Record breakdown by condition / track / distance */}
      <div className="race-history">
        <h2>Records by Condition</h2>
        {records.length === 0 ? (
          <p className="no-history">No record breakdown available.</p>
        ) : (
          <div className="history-table-wrapper">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Starts</th>
                  <th>Wins</th>
                  <th>2nds</th>
                  <th>3rds</th>
                </tr>
              </thead>
              <tbody>
                {records.map((rec) => (
                  <tr key={rec.label} className={rec.firsts > 0 ? 'winner' : ''}>
                    <td className="row-label">{rec.label}</td>
                    <td>{rec.starts}</td>
                    <td className="position">{rec.firsts}</td>
                    <td>{rec.seconds}</td>
                    <td>{rec.thirds}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Trainer / jockey strike-rate stats */}
      {hasConnections && (
        <div className="race-history">
          <h2>Connections (Strike Rate)</h2>
          <div className="history-table-wrapper">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Role</th>
                  <th>Name</th>
                  <th>Strike Rate</th>
                  <th title="Actual to expected">A2E</th>
                  <th>Wins</th>
                  <th>Runners</th>
                </tr>
              </thead>
              <tbody>
                {connectionRows.filter((row) => row.conn).map(({ label, conn }) => (
                  <tr key={label}>
                    <td className="row-label">{label}</td>
                    <td>{conn!.name || '—'}</td>
                    <td className="position">
                      {conn!.strikeRate != null ? `${conn!.strikeRate.toFixed(1)}%` : '—'}
                    </td>
                    <td>{conn!.a2e != null ? conn!.a2e.toFixed(2) : '—'}</td>
                    <td>{conn!.wins}</td>
                    <td>{conn!.runners}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Current / recent race engagements (PF has no past-run detail at this tier) */}
      <div className="race-history">
        <h2>Race Engagements</h2>
        {engagements.length === 0 ? (
          <p className="no-history">No engagements found.</p>
        ) : (
          <div className="history-table-wrapper">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Venue</th>
                  <th>Race</th>
                  <th>Class</th>
                  <th>Dist</th>
                  <th>Brr</th>
                  <th>Wgt</th>
                  <th>Jockey</th>
                  <th>SP</th>
                </tr>
              </thead>
              <tbody>
                {engagements.map((e, index) => (
                  <tr key={index}>
                    <td>{formatDate(e.date)}</td>
                    <td className="venue">
                      <Link to={`/race/${encodeURIComponent(e.venue)}/${e.raceNumber}?date=${encodeURIComponent(e.date || '')}`} className="horse-link">
                        {e.venue || '—'}
                      </Link>
                    </td>
                    <td>R{e.raceNumber}</td>
                    <td className="race-class">{e.raceClass || '—'}</td>
                    <td>{e.distance ? `${e.distance}m` : '—'}</td>
                    <td>{e.barrier || '—'}</td>
                    <td>{e.weight ? `${e.weight}kg` : '—'}</td>
                    <td>{e.jockey || '—'}</td>
                    <td>{e.sp != null ? e.sp.toFixed(2) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>
      )}

    </div>
  );
};

export default HorseDetail;
